import { _ } from "../../../core/polyglot/polyglot.js";
import { schema as baseSchema } from "prosemirror-schema-basic";
import {
	defaultSettings,
	imagePlugin,
	imagePluginKey,
	startImageUpload,
	updateImageNode,
} from "prosemirror-image-plugin";
import { NodeSelection, Plugin, PluginKey } from "prosemirror-state";
import { pictureIcon } from "../../../core/elements/icons/index.js";
import { preloadPicture, toBase64 } from "../../../core/helpers/file-util.js";
import AbstractAction from "./AbstractAction.js";
import alertBus from "../../../core/alerts/alertBus.js";

const supportedMimeTypes = ["image/png", "image/jpeg"];
const insertType = "insert";
const reuploadType = "reupload";

const configuration = {
	injectInput: false,
};

/**
 * Configuration for `ImageAction`.
 *
 * @param {bool} [injectInput] - When true, the input[type=file] for the menu
 *   is actually inserted in the DOM so that Cypress can find it
 */
export function setConfiguration({ injectInput } = {}) {
	configuration.injectInput = injectInput;
}

/**
 * Action to add image support to WYSIWYG editor.
 * It shows a copy of the image as a placeholder with a progress bar on top.
 *
 * When the image is uploaded (via the `uploadPicture` function) the actual iqmage
 * replace the placeholder.
 *
 * It supports adding image via copy/paste and drag'n'drop.
 * It also provides a menu action to insert an image from your file-system.
 *
 * @param {function(File):Promise<string>} onPictureUploadRequest - Function invoked with
 *   a File to upload to the backend. It must resolve to the URL of the uploaded file
 * @param {function(string):Promise<void>} onSourceDeletionRequest - Function invoked
 *   with the URL of a file to delete from its storage
 */
export default class ImageAction extends AbstractAction {
	constructor({ onPictureUploadRequest, onSourceDeletionRequest }) {
		super(...arguments);

		this._currentFileContentPromise = Promise.resolve({
			type: insertType,
			fileContent: "",
		});

		this._imageSettings = {
			...defaultSettings,
			enableResize: false,
			createState: this._createState.bind(this),
			uploadFile: async (fileOrString) => {
				let file = Promise.resolve(fileOrString);
				if (typeof fileOrString === "string") {
					file = this._convertSourceToFile(fileOrString);
				}
				const url = await onPictureUploadRequest(await file);

				await preloadPicture({ url, manualRemove: true });
				return url;
			},
			deleteSrc: onSourceDeletionRequest,
			createOverlay: this._removePreloadFor.bind(this),
			hasTitle: false,
			isBlock: true,
		};
	}

	getNodes() {
		const updatedSchema = updateImageNode(baseSchema.spec.nodes, {
			...this._imageSettings,
		});

		return { image: updatedSchema.get("image") };
	}

	getMenuItems({ actionsManager }) {
		return [this._makeInsertPictureItem({ actionsManager })];
	}

	getPlugins() {
		return [
			new Plugin({
				key: new PluginKey("finsit-picture-placeholder"),
				props: {
					handleDOMEvents: {
						paste: (...args) => {
							this._pasteHandler(...args);
							return false;
						},
						drop: (...args) => {
							this._dropHandler(...args);
							return false;
						},
					},
				},
			}),
			imagePlugin({ ...this._imageSettings }),
			new Plugin({
				key: new PluginKey("finsit-picture-reupload"),
				props: {
					handleDOMEvents: {
						paste: this._pasteUploadHandler.bind(this),
						drop: this._dropUploadHandler.bind(this),
					},
				},
			}),
		];
	}

	/**
	 * Handler to capture the paste event before the plugin does it so that we can get the pasted file.
	 * We will then use this file to build the preview while the image is uploaded.
	 *
	 * This is based on how prose-mirror-plugin handles paste events.
	 *
	 * @see {@link https://gitlab.com/emergence-engineering/prosemirror-image-plugin/-/blob/master/src/plugin/pasteHandler.ts}
	 */
	_pasteHandler(view, event) {
		const clipboardItems = event?.clipboardData?.items;
		if (!clipboardItems) {
			return;
		}
		const items = Array.from(clipboardItems).filter(
			(item) => item.type.indexOf("image") !== -1,
		);
		if (items.length === 0) {
			return;
		}

		const file = items[0].getAsFile();

		if (!file) {
			return;
		}

		if (event?.clipboardData?.types.includes("text/rtf")) {
			return;
		}

		this._currentFileContentPromise = insertFileToBase64(file);
	}

	/**
	 * Handler to capture the drop event before the plugin does it so that we can get the dropped file.
	 * We will then use this file to build the preview while the image is uploaded.
	 *
	 * This is based on how prose-mirror-plugin handles drop events.
	 *
	 * @see {@link https://gitlab.com/emergence-engineering/prosemirror-image-plugin/-/blob/master/src/plugin/dropHandler.ts}
	 */
	_dropHandler(view, event) {
		const file = event?.dataTransfer?.files?.[0];
		const posData = view.posAtCoords({
			top: event.clientY,
			left: event.clientX,
		});

		if (!file || !posData) {
			return;
		}

		this._currentFileContentPromise = insertFileToBase64(file);
	}

	_pasteUploadHandler(view, event) {
		this._reuploadHtmlDataTransferItems({
			view,
			items: event?.clipboardData?.items,
		});

		return false;
	}

	_dropUploadHandler(view, event) {
		this._reuploadHtmlDataTransferItems({
			view,
			items: event.dataTransfer?.items,
		});

		return false;
	}

	_reuploadHtmlDataTransferItems({ view, items }) {
		if (!items) {
			return;
		}

		const filteredItems = [...items].filter(
			(item) => item.type.indexOf("text/html") !== -1,
		);
		if (filteredItems.length === 0) {
			return;
		}

		filteredItems.forEach((item) => {
			item.getAsString((data) => {
				const container = document.createElement("div");
				container.innerHTML = data;
				this._reuploadImagePluginImage({ view, container });
				this._reuploadRawImage({ view, container });
			});
		});
	}

	_reuploadImagePluginImage({ view, container }) {
		const images = [...container.querySelectorAll("div.imagePluginRoot")];
		if (!images.length) {
			return;
		}

		const offset = { value: -1 };

		images.forEach((image) => {
			const source = image.attributes["imageplugin-src"].value;

			this._reuploadFromSource({
				source,
				view,
				alt: image.attributes["imageplugin-alt"]?.value,
				offset,
			});
		});
	}

	_reuploadRawImage({ container }) {
		const image = container.querySelector("img[src]");
		if (!image) {
			return;
		}

		alertBus.warning({
			description: _(
				"Due to security reasons, uploading foreign images is not supported",
			),
		});
	}

	/**
	 * No need for a mapping here, as we actually "replace" the removed node, but we can't track the
	 * plugin transaction.
	 *
	 * @param {string} source - Image source to reupload
	 * @param {ProseMirror.EditorView} view - View of the editor
	 * @param {string} alt - Alternative description of the image
	 * @param {object} offset - Object accumulating position offset. This is a
	 *   bit of ProseMirror black magic. This is also a good candidate in case
	 *   of future bugs :smile:
	 * @param {number} offset.value - Value of the offset. This is passed
	 *   through an object so that it can be mutated in place and simplify the
	 *   code that is already quite low-level
	 */
	_reuploadFromSource({ source, view, alt, offset = { value: -1 } }) {
		const { node, position } = findNode({
			node: view.state.doc,
			skip: 0,
			predicate: (candidate) =>
				candidate.type.name === "image" &&
				candidate.attrs.src === source,
		});

		const resolvedPosition = Math.max(0, position + offset.value);

		this._currentFileContentPromise = Promise.resolve({
			fileContent: source,
			type: reuploadType,
		});

		let transaction = view.state.tr;
		const selection = NodeSelection.create(view.state.doc, position);
		transaction = transaction.setSelection(selection);
		transaction = transaction.deleteSelection();

		view.dispatch(transaction);

		startImageUpload(
			view,
			source,
			alt,
			this._imageSettings,
			view.state.schema,
			resolvedPosition,
		);

		offset.value += node.nodeSize;
	}

	_createState() {
		const base = defaultSettings.createState(...arguments);

		return {
			init(...args) {
				return base.init(...args);
			},
			apply: (...args) => {
				const [transaction] = args;
				const action = transaction.getMeta(imagePluginKey);

				const result = base.apply(...args);

				if (action?.type === "add") {
					const container = result.find()[0].type.toDOM;

					this._currentFileContentPromise.then(
						({ type, fileContent }) => {
							if (!fileContent) return;

							const previewContainer =
								this._makeImagePreviewWithLoader({
									fileContent,
								});

							container.appendChild(previewContainer);

							if (type === reuploadType) {
								container.classList.add("reupload");
							}
							if (!container.parentElement.innerText.trim()) {
								container.classList.add("alone");
							}
						},
					);
				}

				return result;
			},
		};
	}

	_makeImagePreviewWithLoader({ fileContent }) {
		const image = document.createElement("img");
		image.setAttribute("src", fileContent);

		const loaderContainer = document.createElement("div");
		loaderContainer.classList.add("loader-container");

		const loader = document.createElement("div");
		loader.classList.add("picture-loader");

		loaderContainer.appendChild(loader);

		const previewContainer = document.createElement("div");
		previewContainer.classList.add("preview-container");

		previewContainer.appendChild(image);
		previewContainer.appendChild(loaderContainer);

		return previewContainer;
	}

	_removePreloadFor(node) {
		const source = node?.attrs?.src;
		if (!source) {
			return;
		}

		const preload = document.querySelector(`img.preload[src="${source}"]`);
		if (!preload) {
			return;
		}
		preload.parentElement?.removeChild(preload);
	}

	async _convertSourceToFile(source) {
		const result = await fetch(source);
		const blob = await result.blob();
		return new File([blob], "Upload", {
			type: blob.type,
			lastModified: Date.now(),
		});
	}

	_makeInsertPictureItem() {
		return this._makeMenuItem({
			order: 90,
			title: _("Insert Picture"),
			run: (state, dispatch, view) => {
				const input = document.createElement("input");
				input.setAttribute("type", "file");
				input.setAttribute("accept", supportedMimeTypes.join(", "));

				input.addEventListener("change", (event) => {
					const file = event?.target?.files[0];
					if (!file) return;

					this._currentFileContentPromise = insertFileToBase64(file);

					startImageUpload(
						view,
						file,
						file.name,
						this._imageSettings,
						state.schema,
						state.selection.from,
					);
				});

				if (configuration.injectInput) {
					// Cypress requires that the input is actually inserted in the DOM
					// while in practice, it's not needed. Since it's Cypress only
					// we don't care too much about cleaning the DOM afterward
					input.style.position = "fixed";

					input.style.top =
						"-100000000000000000000000000000000000000px";

					input.style.left =
						"-100000000000000000000000000000000000000px";

					document.body.appendChild(input);
				}

				input.click();
			},
			icon: this._makeIcon({ cssClass: pictureIcon.getCssClass() }),
		});
	}

	static convertSourceToViewerContent(source) {
		return this._iterateOverImagesFromSource({
			source,
			fn: (div) => {
				const src =
					div.attributes.getNamedItem("imageplugin-src").value;
				const alt =
					div.attributes.getNamedItem("imageplugin-alt")?.value;

				const img = document.createElement("img");
				img.src = src;
				img.setAttribute("alt", alt);

				div.appendChild(img);
			},
		});
	}

	static convertSourceToPDFExporter(source) {
		return this._iterateOverImagesFromSource({
			source,
			fn: (div) => {
				const src =
					div.attributes.getNamedItem("imageplugin-src").value;
				const img = document.createElement("img");
				img.src = src;

				div.parentElement.insertBefore(img, div);
				div.parentElement.removeChild(div);
			},
		});
	}

	static _iterateOverImagesFromSource({ source, fn }) {
		const node = document.createElement("div");
		node.innerHTML = source;

		const allImages = [...node.querySelectorAll("div[imageplugin-src]")];

		allImages.forEach(fn);

		return node.innerHTML;
	}
}

function insertFileToBase64(file) {
	return toBase64(file).then((fileContent) => ({
		type: insertType,
		fileContent,
	}));
}

function findNode({ node, skip = 0, predicate }) {
	let found;

	node.descendants((child, position) => {
		if (predicate(child)) {
			found = { node: child, position };
		}
		if (found) {
			if (skip > 0) {
				skip--;
				return undefined;
			}
			return false;
		}
		return undefined;
	});

	return found;
}
